import { useCallback, useSyncExternalStore } from "react";
import type { RestrictedWord } from "@/shared/cache/slices/restrictedWords";
import { createSpellingStore } from "@/shared/spellcheck/store";
import { logger } from "@/shared/utils/logger";
import {
	detectRestrictedWords,
	mergeOverlappingMatches,
} from "@/shared/utils/restrictedWords";

const spelling = createSpellingStore(
	() =>
		new Worker(new URL("../spellcheck/spellcheck.worker.ts", import.meta.url), {
			type: "module",
		}),
	() =>
		logger.captureException(new Error("Narrative spellchecker unavailable"), {
			action: "spellcheck",
		}),
);
const getServerSnapshot = () => undefined;

export function SpellcheckedNarrative({
	text,
	className,
	debounceMs = 0,
	restrictedWords = [],
}: {
	text: string;
	className?: string;
	/** Wait for generated text to settle before checking it. */
	debounceMs?: number;
	restrictedWords?: RestrictedWord[];
}) {
	const subscribe = useCallback(
		(notify: () => void) => {
			if (!debounceMs) return spelling.subscribe(text, notify);
			let unsubscribe: (() => void) | undefined;
			const timeout = setTimeout(() => {
				unsubscribe = spelling.subscribe(text, notify);
				notify();
			}, debounceMs);
			return () => {
				clearTimeout(timeout);
				unsubscribe?.();
			};
		},
		[text, debounceMs],
	);
	const getSnapshot = useCallback(() => spelling.getSnapshot(text), [text]);
	const ranges = useSyncExternalStore(
		subscribe,
		getSnapshot,
		getServerSnapshot,
	);
	return (
		<p className={className}>
			{renderNarrativeMarks(text, ranges ?? [], restrictedWords)}
		</p>
	);
}

/** Split on both sets of boundaries so spelling and compliance never erase each other. */
export function renderNarrativeMarks(
	text: string,
	spellingRanges: readonly { start: number; end: number }[],
	restrictedWords: RestrictedWord[],
) {
	const matches = mergeOverlappingMatches(
		detectRestrictedWords(text, restrictedWords),
	);
	const boundaries = [
		...new Set([
			0,
			text.length,
			...spellingRanges.flatMap((r) => [r.start, r.end]),
			...matches.flatMap((r) => [r.start, r.end]),
		]),
	].sort((a, b) => a - b);
	return boundaries.slice(0, -1).map((start, i) => {
		const end = boundaries[i + 1];
		const misspelled = spellingRanges.some(
			(r) => r.start <= start && r.end >= end,
		);
		const match = matches.find((r) => r.start <= start && r.end >= end);
		const content = (
			<span
				key={start}
				aria-invalid={misspelled ? "spelling" : undefined}
				className={
					misspelled
						? "underline decoration-wavy decoration-red-500 underline-offset-[3px]"
						: undefined
				}
			>
				{text.slice(start, end)}
			</span>
		);
		return match ? (
			<mark
				key={start}
				title={[
					match.severity === "block" ? "Blocked" : "Warning",
					match.displayName,
					match.suggestion,
				]
					.filter(Boolean)
					.join(" — ")}
				className={
					match.severity === "block"
						? "rounded-sm bg-red-200/70 dark:bg-red-500/25 text-inherit"
						: "rounded-sm bg-amber-200/70 dark:bg-amber-500/25 text-inherit"
				}
			>
				{content}
			</mark>
		) : (
			<span key={start}>{content}</span>
		);
	});
}
